import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../utils/axiosConfig';
import toast from 'react-hot-toast';
import '../styles/MemberDirectory.css';

interface MemberProfile {
  id: string;
  userId: string;
  displayName: string | null;
  headline: string | null;
  bio: string | null;
  avatarUrl: string | null;
  linkedInUrl: string | null;
  portfolioUrl: string | null;
  location: string | null;
  isPublic: boolean;
  showEmail: boolean;
  skills: string[];
  lookingFor: string[];
  industries: string[];
  experienceLevel: string | null;
  viewCount: number;
  user?: { email: string };
}

const MemberDirectory: React.FC = () => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<MemberProfile[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [skillFilter, setSkillFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [page, setPage] = useState(0);
  const [selectedProfile, setSelectedProfile] = useState<MemberProfile | null>(null);
  const [connectingTo, setConnectingTo] = useState<string | null>(null);
  const [connectMessage, setConnectMessage] = useState('');
  const [showConnectModal, setShowConnectModal] = useState(false);
  const PAGE_SIZE = 12;

  const fetchProfiles = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        limit: PAGE_SIZE.toString(),
        offset: (page * PAGE_SIZE).toString(),
      };
      if (search) params.search = search;
      if (skillFilter) params.skills = skillFilter;
      if (levelFilter) params.experienceLevel = levelFilter;

      const response = await axiosInstance.get('/auth/community/profiles', { params });
      setProfiles(response.data.profiles || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [page, search, skillFilter, levelFilter]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const viewProfile = async (profileId: string) => {
    try {
      const response = await axiosInstance.get(`/auth/community/profiles/${profileId}`);
      setSelectedProfile(response.data.profile);
    } catch (err) {
      toast.error('Failed to load profile');
    }
  };

  const handleConnect = async () => {
    if (!connectingTo) return;
    if (connectMessage.length > 1000) { toast.error('Message is too long (max 1,000 characters)'); return; }
    try {
      await axiosInstance.post('/auth/community/connections/request', {
        receiverId: connectingTo,
        message: connectMessage.trim() || undefined,
      });
      toast.success('Connection request sent!');
      setShowConnectModal(false);
      setConnectMessage('');
      setConnectingTo(null);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to send request');
    }
  };

  const openConnectModal = (userId: string) => {
    setConnectingTo(userId);
    setConnectMessage('');
    setShowConnectModal(true);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    fetchProfiles();
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const getInitials = (name: string | null) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getLevelLabel = (level: string | null) => {
    const map: Record<string, string> = {
      junior: 'Junior',
      mid: 'Mid-Level',
      senior: 'Senior',
      executive: 'Executive',
    };
    return level ? map[level] || level : '';
  };

  return (
    <div className="md-container">
      <div className="md-header">
        <button className="md-back-btn" onClick={() => navigate('/community')}>
          ← Back to Community
        </button>
        <h1>Member Directory</h1>
        <p>Discover and connect with fellow job seekers</p>
      </div>

      {/* Filters */}
      <form className="md-filters" onSubmit={handleSearch}>
        <input
          type="text"
          placeholder="Search by name, headline, or bio..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="md-search-input"
        />
        <input
          type="text"
          placeholder="Filter by skill (e.g., React)"
          value={skillFilter}
          onChange={e => setSkillFilter(e.target.value)}
          className="md-skill-input"
        />
        <select
          value={levelFilter}
          onChange={e => { setLevelFilter(e.target.value); setPage(0); }}
          className="md-level-select"
        >
          <option value="">All Levels</option>
          <option value="junior">Junior</option>
          <option value="mid">Mid-Level</option>
          <option value="senior">Senior</option>
          <option value="executive">Executive</option>
        </select>
        <button type="submit" className="md-search-btn">Search</button>
      </form>

      {/* Results */}
      {loading ? (
        <div className="md-loading">Loading members...</div>
      ) : profiles.length === 0 ? (
        <div className="md-empty">
          <p>No public profiles found. Try adjusting your filters.</p>
        </div>
      ) : (
        <>
          <p className="md-count">{total} member{total !== 1 ? 's' : ''} found</p>
          <div className="md-grid">
            {profiles.map(p => (
              <div key={p.id} className="md-card" onClick={() => viewProfile(p.id)}>
                <div className="md-avatar">
                  {p.avatarUrl ? (
                    <img src={p.avatarUrl} alt={p.displayName || ''} />
                  ) : (
                    <span className="md-initials">{getInitials(p.displayName)}</span>
                  )}
                </div>
                <h3>{p.displayName || 'Anonymous'}</h3>
                {p.headline && <p className="md-headline">{p.headline}</p>}
                {p.location && <span className="md-location">📍 {p.location}</span>}
                {p.experienceLevel && (
                  <span className="md-level">{getLevelLabel(p.experienceLevel)}</span>
                )}
                {p.skills.length > 0 && (
                  <div className="md-skills">
                    {p.skills.slice(0, 4).map(s => (
                      <span key={s} className="md-skill-tag">{s}</span>
                    ))}
                    {p.skills.length > 4 && (
                      <span className="md-skill-more">+{p.skills.length - 4}</span>
                    )}
                  </div>
                )}
                <div className="md-card-footer">
                  <span className="md-views">👁 {p.viewCount}</span>
                  <button
                    className="md-connect-btn"
                    onClick={e => { e.stopPropagation(); openConnectModal(p.userId); }}
                  >
                    Connect
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="md-pagination">
              <button disabled={page === 0} onClick={() => setPage(page - 1)}>← Previous</button>
              <span>Page {page + 1} of {totalPages}</span>
              <button disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>Next →</button>
            </div>
          )}
        </>
      )}

      {/* Profile Detail Modal */}
      {selectedProfile && (
        <div className="md-modal-overlay" onClick={() => setSelectedProfile(null)}>
          <div className="md-modal" onClick={e => e.stopPropagation()}>
            <button className="md-modal-close" onClick={() => setSelectedProfile(null)}>✕</button>
            <div className="md-modal-header">
              <div className="md-modal-avatar">
                {selectedProfile.avatarUrl ? (
                  <img src={selectedProfile.avatarUrl} alt={selectedProfile.displayName || ''} />
                ) : (
                  <span className="md-initials lg">{getInitials(selectedProfile.displayName)}</span>
                )}
              </div>
              <div>
                <h2>{selectedProfile.displayName || 'Anonymous'}</h2>
                {selectedProfile.headline && <p className="md-modal-headline">{selectedProfile.headline}</p>}
                {selectedProfile.location && <p className="md-modal-location">📍 {selectedProfile.location}</p>}
              </div>
            </div>

            {selectedProfile.bio && (
              <div className="md-modal-section">
                <h3>About</h3>
                <p>{selectedProfile.bio}</p>
              </div>
            )}

            {selectedProfile.experienceLevel && (
              <div className="md-modal-section">
                <h3>Experience</h3>
                <p>{getLevelLabel(selectedProfile.experienceLevel)}</p>
              </div>
            )}

            {selectedProfile.skills.length > 0 && (
              <div className="md-modal-section">
                <h3>Skills</h3>
                <div className="md-skills">
                  {selectedProfile.skills.map(s => (
                    <span key={s} className="md-skill-tag">{s}</span>
                  ))}
                </div>
              </div>
            )}

            {selectedProfile.lookingFor.length > 0 && (
              <div className="md-modal-section">
                <h3>Looking For</h3>
                <div className="md-skills">
                  {selectedProfile.lookingFor.map(l => (
                    <span key={l} className="md-skill-tag green">{l}</span>
                  ))}
                </div>
              </div>
            )}

            {selectedProfile.industries.length > 0 && (
              <div className="md-modal-section">
                <h3>Industries</h3>
                <div className="md-skills">
                  {selectedProfile.industries.map(i => (
                    <span key={i} className="md-skill-tag blue">{i}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="md-modal-links">
              {selectedProfile.linkedInUrl && (
                <a href={selectedProfile.linkedInUrl} target="_blank" rel="noopener noreferrer">
                  🔗 LinkedIn
                </a>
              )}
              {selectedProfile.portfolioUrl && (
                <a href={selectedProfile.portfolioUrl} target="_blank" rel="noopener noreferrer">
                  🌐 Portfolio
                </a>
              )}
              {selectedProfile.user?.email && (
                <a href={`mailto:${selectedProfile.user.email}`}>
                  ✉️ {selectedProfile.user.email}
                </a>
              )}
            </div>

            <div className="md-modal-actions">
              <button
                className="md-connect-btn lg"
                onClick={() => openConnectModal(selectedProfile.userId)}
              >
                🤝 Send Connection Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Connect Request Modal */}
      {showConnectModal && (
        <div className="md-modal-overlay" onClick={() => setShowConnectModal(false)}>
          <div className="md-modal sm" onClick={e => e.stopPropagation()}>
            <h2>Send Connection Request</h2>
            <p className="md-connect-hint">Add a personal message (optional)</p>
            <textarea
              value={connectMessage}
              onChange={e => setConnectMessage(e.target.value)}
              placeholder="Hi! I'd love to connect..."
              rows={3}
            />
            <div className="md-modal-actions">
              <button className="md-cancel-btn" onClick={() => setShowConnectModal(false)}>Cancel</button>
              <button className="md-send-btn" onClick={handleConnect}>Send Request</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberDirectory;
